import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const previewAnimation = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8, transition: { duration: 0.3 } },
    transition: { duration: 0.3 },
};

const copiesAnimation = {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 50, transition: { duration: 0.3 } },
    transition: { duration: 0.3 },
};

function DocumentPreview({
    currentDocument,
    copyCount,
    setDocSettings,
    docId,
    setCopyCount, // Добавляем setCopyCount для EditDoc
    pdfLogo,
    docLogo,
    pptxLogo,
    xlsxLogo,
    t,
}) {
    const { t: translate } = useTranslation(); // Используем локальный t для переводов

    if (!currentDocument) {
        return (
            <div className="flex flex-col items-center">
                <motion.div
                    className="w-28 h-28 border-4 border-t-4 border-t-blue-500 border-gray-200 rounded-full animate-spin mb-4"
                />
                <p className="text-white text-[2vh]">{t("editManyDocs.loading")}</p>
            </div>
        );
    }

    const getFileIcon = (fileName) => {
        const lowerFileName = fileName?.toLowerCase();
        if (lowerFileName?.endsWith(".pdf")) return pdfLogo;
        if (lowerFileName?.endsWith(".doc") || lowerFileName?.endsWith(".docx"))
            return docLogo;
        if (lowerFileName?.endsWith(".ppt") || lowerFileName?.endsWith(".pptx"))
            return pptxLogo;
        if (lowerFileName?.endsWith(".xls") || lowerFileName?.endsWith(".xlsx"))
            return xlsxLogo;
        return pdfLogo;
    };

    const handleCopyCountChange = (increment) => {
        const newCount = increment ? copyCount + 1 : Math.max(1, copyCount - 1);
        if (setDocSettings && docId) {
            // Для EditManyDocs
            setDocSettings((prev) => ({
                ...prev,
                [docId]: {
                    ...prev[docId],
                    copyCount: newCount,
                },
            }));
        } else if (setCopyCount) {
            // Для EditDoc
            setCopyCount(newCount);
        }
    };

    const statusKey =
        currentDocument.status === "printed"
            ? "printed"
            : currentDocument.status === "conversion_error"
            ? "conversion_failed"
            : "ready";

    const statusStyles = {
        printed: "bg-green-500 text-white",
        conversion_failed: "bg-red-500 text-white",
        ready: "bg-blue-500 text-white",
    };

    return (
        <motion.div {...previewAnimation} className="ml-[2vw] mt-[2vh] flex">
            <div className="bg-[#FFFFFF30] h-[12vh] w-[20vw] rounded-lg overflow-hidden">
                <img
                    src={getFileIcon(currentDocument.file_name)}
                    alt={t("editDoc.document_preview")}
                    className="h-full w-full object-cover"
                />
            </div>
            <motion.div
                {...copiesAnimation}
                className="flex flex-col text-[2.3vh] font-bold ml-[4vw] mt-[2vh]"
            >
                <div className="text-white flex items-center gap-3">
                    {t("editDoc.file")}:&nbsp;
                    <span
                        className={`text-black ${
                            currentDocument.file_name.length > 30
                                ? "text-[1.8vh]"
                                : "text-[2.3vh]"
                        } ${
                            currentDocument.file_name.length > 50
                                ? "truncate"
                                : ""
                        } max-w-[40vw] whitespace-nowrap overflow-hidden`}
                        title={currentDocument.file_name}
                    >
                        {currentDocument.file_name.length > 50
                            ? `${currentDocument.file_name.slice(0, 47)}...`
                            : currentDocument.file_name}
                    </span>
                    <span
                        className={`text-[1.4vh] font-semibold px-3 py-1 rounded-full whitespace-nowrap ${statusStyles[statusKey]}`}
                    >
                        {translate(`docItems.status.${statusKey}`)}
                    </span>
                </div>
                <div className="text-white flex flex-wrap gap-x-[3vw] mt-[1vh] ">
                    {t("editDoc.copies")}:
                    <button
                        className="h-[3.5vh] w-[7vw] rounded-lg p-1 ml-[2.5vw]"
                        onClick={() => handleCopyCountChange(false)}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 16 16"
                            fill="#D9D9D9"
                            className="w-full h-full"
                        >
                            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0" />
                            <path
                                d="M4.5 7.5a.5.5 0 0 0 0 1h7a.5.5 0 0 0 0-1z"
                                fill="#000"
                            />
                        </svg>
                    </button>
                    <span className="text-[2.3vh] text-black">{copyCount}</span>
                    <button
                        className="h-[3.5vh] w-[7vw] rounded-lg p-1"
                        onClick={() => handleCopyCountChange(true)}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 16 16"
                            fill="#D9D9D9"
                            className="w-full h-full"
                        >
                            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0" />
                            <path
                                d="M8.5 4.5a.5.5 0 0 0-1 0v3h-3a.5.5 0 0 0 0 1h3v3a.5.5 0 0 0 1 0v-3h3a.5.5 0 0 0 0-1h-3z"
                                fill="#000"
                            />
                        </svg>
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
}

export default DocumentPreview;